// A word take's stress check goes with its upload (listening cards: a partner's take may voice my
// listening card once its stress check passed; api routes/listen-voices.ts). The check runs on the
// device before the take is saved (pronunciation/PronunciationAnalysis.tsx); its summary is kept
// here by the take's audio, and the upload path carries it as `&stress=<ok>/<checked>` — also when
// the take waits in the outbox (the path is stored with it). Word takes ("lemma:<id>") only.
import { useEffect } from 'react';
import { recordingSummary, type AnalysisLike } from '@valyrio/engine/pedagogy/logs';

const byBlob = new WeakMap<Blob, { ok: number; checked: number }>();

/** Remember the stress check of a word or form take's audio (nothing for other targets or nothing judged). */
export function noteTakeStress(target: string | undefined, blob: Blob | null, results: AnalysisLike | null | undefined): void {
  // word takes, and form takes of the inflection tables (a form's listening card, 2026-10-09)
  if (!(target?.startsWith('lemma:') || target?.startsWith('form:')) || !blob || !results) return;
  const s = recordingSummary(results);
  if (!s || !s.stressChecked) return;
  byBlob.set(blob, { ok: s.stressOk, checked: s.stressChecked });
}

/** The same check for the copy of the audio that is uploaded (uploadTake re-types the blob). */
export function carryTakeStress(from: Blob, to: Blob): void {
  const s = byBlob.get(from);
  if (s) byBlob.set(to, s);
}

/** The upload's query part ("&stress=1/1"), or '' when the audio has no check. */
export function takeStressQuery(blob: Blob | undefined): string {
  const s = blob ? byBlob.get(blob) : undefined;
  return s ? `&stress=${s.ok}/${s.checked}` : '';
}

/** PronunciationAnalysis: remember the check once the analysis is done. */
export function useTakeStress(target: string | undefined, blob: Blob | null, results: AnalysisLike | null | undefined): void {
  useEffect(() => noteTakeStress(target, blob, results), [target, blob, results]);
}
